<template>
    <div class="registered-deal-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3 v-if="editable">{{ registeredDeal.id ? 'Edit' : 'Add'}} Registered Deal</h3>
                <h3 v-else>Registered Deal Details</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />
                <div v-else>
                    <div class="input-container agency">
                        <label>Agency</label>
                        <agency-search
                            v-model="registeredDeal.agency"
                            :clientId="clientId"
                            v-if="editable" 
                            @input="handleAgencyChange"
                        />
                        <div v-else>{{ registeredDeal.agency.name }}</div>
                        <div class="red-accent error-message" v-if="$v.registeredDeal.agency.$error">You must select an agency</div>
                    </div>
                    <div v-if="agencyHasMDFRequests" class="input-container agency">
                        <label>Is this opportunity connected to an MDF?</label>
                        <app-select-dropdown 
                            v-model="selectedMDFRequest" 
                            placeholder="Select Fund Title"
                            :items="mdfRequests"
                        />
                    </div>
                    <div class="input-container registration-number">
                        <label>Registration Number</label>
                        <app-input
                            v-model="registeredDeal.registrationNumber"
                            v-if="editable" />
                        <div v-else>{{ registeredDeal.registrationNumber }}</div>
                    </div>
                    <div class="input-container name">
                        <label>Name</label>
                        <app-input
                            v-model="registeredDeal.name"
                            v-if="editable" />
                        <div v-else>{{ registeredDeal.name }}</div>
                    </div>
                    <div class="input-container amount">
                        <label>Amount</label>
                        <app-input
                            v-model="registeredDeal.value"
                            :type="'number'"
                            :placeholder="'$5,000'"
                            v-if="editable" />
                        <div v-else>{{ registeredDeal.value }}</div>
                    </div>
                    <div class="input-container description">
                        <label>Description</label>
                        <app-textarea
                            v-model="registeredDeal.description"
                            v-if="editable" />
                        <div v-else>{{ registeredDeal.description }}</div>
                    </div>
                    <div class="input-container status" v-if="!isClientUser || registeredDeal.id">
                        <label>Status</label>
                        <div class="status-wrapper" v-if="editable">
                            <div v-if="isClientUser">{{ registeredDeal.status ? registeredDeal.status.name : "" }}</div>
                            <registered-deal-status-dropdown
                                v-else
                                v-model="registeredDeal.status" />
                        </div>
                        <div v-else>{{ registeredDeal.status.name }}</div>
                    </div>
                    <div class="input-container reason" v-if="registeredDeal.status && registeredDeal.status.value === false">
                        <label>Reason</label>
                        <div class="reason-wrapper" v-if="editable">
                            <div v-if="isClientUser">{{ registeredDeal.reason || 'No reason given.' }}</div>
                            <app-textarea
                                v-else
                                v-model="registeredDeal.reason" />
                        </div>
                        <div v-else>{{ registeredDeal.reason }}</div>
                    </div>
                </div>
            </div>
            <div class="widget-footer" v-if="editable">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container" v-if="editable">
                    <app-button @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const AppSelectDropdown = () => import('@/components/common/app-select-dropdown');
const AppTextArea = () => import('@/components/common/app-textarea');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AgencySearch = () => import('@/components/agency/agency-search');
const RegisteredDealStatusDropdown = () => import('@/components/opportunity/registered-deal-status-dropdown');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import { required } from 'vuelidate/lib/validators';
import EventBus from '@/scripts/event-bus.js';
import { ClientManagementService } from '@/scripts/services/mdf/client/client-management-service.js'

export default {
    name: 'registered-deal-widget',

    props: {
        options: {
            type: Object
        },

        widget: {
            type: Object,
            required: true
        }
    },

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-select-dropdown': AppSelectDropdown,
        'app-textarea': AppTextArea,
        'widget-close-svg': WidgetCloseSvg,
        'agency-search': AgencySearch,
        'registered-deal-status-dropdown': RegisteredDealStatusDropdown,
        'loading-screen-svg': LoadingScreenSvg
    },

    data() {
        return {
            isLoading: false,

            clientId: this.widget.options.clientId,

            registeredDeal: {
                id: this.widget.options.registeredDealId,
                opportunityId: this.widget.options.opportunityId,
                name: null,
                value: null,
                registrationNumber: null,
                status: {
                    name: 'Pending',
                    value: null
                },
                agency: null,
                description: null,
                reason: null,
            },

            mdfRequests: [],

            selectedMDFRequest: {},

            editable: this.widget.options.editable === undefined ? true : this.widget.options.editable
        }
    },

    validations: {
        registeredDeal: {
            agency: {
                required
            }
        },
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        isClientUser() {
            return this.$store.getters.isClientUser;
        },

        agencyHasMDFRequests() {
            return this.mdfRequests.length > 0;
        }
    },

    methods: {
        save() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.isLoading = true;

            if(this.registeredDeal.id) {
                this.patch();
            } 
            else {
                this.post();
            }
        },

        patch() {
            var changes = [{
                'name': 'Name',
                'value': this.registeredDeal.name
            }, {
                'name': 'Description',
                'value': this.registeredDeal.description
            }, {
                'name': 'Value',
                'value': this.registeredDeal.value
            }, {
                'name': 'IsApproved',
                'value': this.registeredDeal.status.value
            }, {
                'name': 'RegistrationNumber',
                'value': this.registeredDeal.registrationNumber
            }, {
                'name': 'AgencyId',
                'value': this.registeredDeal.agency.id
            }, {
                'name': 'Reason',
                'value': this.registeredDeal.status.value === false ? this.registeredDeal.reason : null
            }, {
                'name': 'FundRequestId',
                'value': this.selectedMDFRequest.id
            }, {
                'name': 'FundRequestName',
                'value': this.selectedMDFRequest.name
            }];

            this.$api
                .patch(`/api/RegisteredDeals/${ this.registeredDeal.id }`, changes)
                .then(response => {
                    this.$helpers.showSuccessNotification('Your registered deal has been successfully updated.');
                    EventBus.$emit('registered-deal:update');
                    EventBus.$emit('opportunity:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while updating your registered deal.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        post() {
            var registeredDeal = {
                Name: this.registeredDeal.name,
                IsApproved: this.registeredDeal.status.value,
                Value: parseFloat(this.registeredDeal.value || 0),
                Description: this.registeredDeal.description,
                RegistrationNumber: this.registeredDeal.registrationNumber,
                Reason: this.registeredDeal.status.value === false ? this.registeredDeal.reason : null,
                DealId: this.widget.options.opportunityId,
                AgencyId: this.registeredDeal.agency.id,
                FundRequestId: this.selectedMDFRequest.id,
                FundRequestName: this.selectedMDFRequest.name,
            }

            this.$api
                .post(`/api/RegisteredDeals`, registeredDeal)
                .then(response => {
                    this.$helpers.showSuccessNotification('Your registered deal has been successfully created.');
                    EventBus.$emit('registered-deal:update');
                    EventBus.$emit('opportunity:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('An error occurred while creating your registered deal.', { error });
                });
        },

        getStatusName(isApproved) {
            if(isApproved === true) {
                return 'Approved';
            } 
            else if (isApproved === false) {
                return 'Denied';
            }
            else {
                return 'Pending';
            }
        },

        getRegisteredDeal() {
            this.isLoading = true;

            this.$api
                .get(`/service/RegisteredDeals?$filter=Id eq ${ this.registeredDeal.id }&$expand=Agency($select=Id,Name,LogoImageUrl)`)
                .then(response => {
                    this.registeredDeal.name = response.data.value[0].Name;
                    this.registeredDeal.value = response.data.value[0].Value;
                    this.registeredDeal.registrationNumber = response.data.value[0].RegistrationNumber;
                    this.registeredDeal.description = response.data.value[0].Description;
                    this.registeredDeal.reason = response.data.value[0].Reason;
                    
                    this.registeredDeal.agency = {
                        id: response.data.value[0].Agency.Id,
                        name: response.data.value[0].Agency.Name,
                        logoImageUrl: response.data.value[0].Agency.LogoImageUrl
                    };
                    
                    this.registeredDeal.status = {
                        name: this.getStatusName(response.data.value[0].IsApproved),
                        value: response.data.value[0].IsApproved
                    };

                    this.selectedMDFRequest = {
                        id: response.data.value[0].FundRequestId,
                        name: response.data.value[0].FundRequestName,
                    };

                    return this.handleAgencyChange();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while fetching your registered deal.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        handleAgencyChange() {
            const config = {
                where: `[{"AgencyId": ${this.registeredDeal.agency.id}}]`
            };
            return ClientManagementService.getAll({ accountId: this.accountId, config })
                .then(({ data: { data } }) => {
                    this.mdfRequests = data.map(({ Name, Id }) => ({name: Name, id: Id}));
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your MDF requests.',
                        { error },
                    );
                })
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        }
    },

    created() {
        if (this.registeredDeal.id) {
            this.getRegisteredDeal();
        }
    }
}
</script>

<style>
.registered-deal-widget .input-container {
    margin-bottom: 15px;
}

.registered-deal-widget .cancel-btn {
    background: transparent;
    color: #877D88;
    border: none;
}

.registered-deal-widget .cancel-btn:hover {
    background: #E1E0E5;
}

.registered-deal-widget .loading-screen {
    text-align: center;
}

.registered-deal-widget .error-message {
    margin-top: 8px;
    font-size: 12px;
}

.registered-deal-widget .app-select.v-input.v-input--is-focused .v-input__slot{
    padding: 0 10px 0 15px;
}

.registered-deal-widget .app-select-dropdown .app-select.v-input > .v-input__control > .v-input__slot{
    height: 40px;
}
</style>